<script lang="ts" setup>
const { $t } = useNuxtApp()

// Accessibility: Track autocomplete dropdown state for screen reader announcements (WCAG 4.1.2)
const suggestionsAnnouncement = ref<string>('')
let pacContainerObserver: MutationObserver | null = null
let inputElement: HTMLInputElement | null = null
let lastAnnouncedCount = 0
let announceTimeout: ReturnType<typeof setTimeout> | null = null

/**
 * Observe the Google Maps autocomplete dropdown (.pac-container) for visibility changes
 * and announce when suggestions become available (WCAG 4.1.2, 1.3.1)
 */
const setupDropdownObserver = (): void => {
    // Google Maps creates .pac-container dynamically in the body
    const observerCallback = (): void => {
        const pacContainer = document.querySelector('.pac-container') as HTMLElement
        if (!pacContainer) return

        // Check if dropdown is visible (has items)
        const isVisible = pacContainer.style.display !== 'none' &&
            pacContainer.offsetHeight > 0
        const items = pacContainer.querySelectorAll('.pac-item')

        if (isVisible && items.length > 0) {
            // Set aria-expanded on input
            if (inputElement) {
                inputElement.setAttribute('aria-expanded', 'true')
            }

            // Only announce if count changed (debounced to avoid excessive announcements)
            if (items.length !== lastAnnouncedCount) {
                if (announceTimeout) {
                    clearTimeout(announceTimeout)
                }
                announceTimeout = setTimeout(() => {
                    lastAnnouncedCount = items.length
                    suggestionsAnnouncement.value = ''
                    nextTick(() => {
                        suggestionsAnnouncement.value = `${$t('Nájdených')} ${items.length} ${$t('návrhov miest')}. ${$t('Použite šípky hore a dole pre navigáciu a Enter pre výber.')}`
                    })
                }, 300)
            }

            // Add role="listbox" to container and role="option" to items for better screen reader support
            if (!pacContainer.getAttribute('role')) {
                pacContainer.setAttribute('role', 'listbox')
                pacContainer.setAttribute('aria-label', $t('Návrhy lokalít') as string)
            }
            items.forEach((item, index) => {
                if (!item.getAttribute('role')) {
                    item.setAttribute('role', 'option')
                    item.setAttribute('id', `pac-item-${index}`)
                }
            })
        } else {
            if (inputElement) {
                inputElement.setAttribute('aria-expanded', 'false')
            }
            // Reset count when dropdown closes
            lastAnnouncedCount = 0
            suggestionsAnnouncement.value = ''
        }
    }

    // Observe body for .pac-container changes (it's dynamically added)
    pacContainerObserver = new MutationObserver(observerCallback)
    pacContainerObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
    })
}

onMounted(() => {
    nextTick(() => {
        inputElement = document.querySelector('.pac-target-input') as HTMLInputElement
        if (inputElement) {
            inputElement.setAttribute('role', 'combobox')
            inputElement.setAttribute('aria-autocomplete', 'list')
            inputElement.setAttribute('aria-haspopup', 'listbox')
            inputElement.setAttribute('aria-expanded', 'false')
        }

        setupDropdownObserver()
    })
})

onUnmounted(() => {
    if (pacContainerObserver) {
        pacContainerObserver.disconnect()
        pacContainerObserver = null
    }
    if (announceTimeout) {
        clearTimeout(announceTimeout)
        announceTimeout = null
    }
})
</script>
<template>
  <div class="gmap-autocomplete-wrapper">
    <slot />
    <!-- Accessibility: Screen reader announcement for autocomplete suggestions (WCAG 4.1.2, 1.3.1) -->
    <div
      aria-live="polite"
      aria-atomic="true"
      class="sr-only"
    >
      {{ suggestionsAnnouncement }}
    </div>
  </div>
</template>
<style lang="scss">
@import './GMapAutocompleteWrapper.scss';
</style>
